import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import { Toaster } from "sonner";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import RoleSelect from "./pages/RoleSelect";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

import SellerDashboard from "./pages/seller/SellerDashboard";
import SellerCompanies from "./pages/seller/SellerCompanies";
import NewCompany from "./pages/seller/NewCompany";
import CompanyWizard from "./pages/seller/CompanyWizard";
import ConfidentialityNDA from "./pages/seller/ConfidentialityNDA";
import Questionnaire from "./pages/seller/Questionnaire";
import TeaserReview from "./pages/seller/TeaserReview";
import ImReview from "./pages/seller/ImReview";
import ImView from "./pages/ImView";

import SearcherDashboard from "./pages/searcher/SearcherDashboard";
import SearcherOnboarding from "./pages/searcher/SearcherOnboarding";
import CompanyDetail from "./pages/searcher/CompanyDetail";
import SearcherPipeline from "./pages/searcher/SearcherPipeline";

import InvestorOnboarding from "./pages/investor/InvestorOnboarding";
import InvestorMarketplace from "./pages/investor/InvestorMarketplace";
import InvestorOpportunityDetail from "./pages/investor/InvestorOpportunityDetail";
import InvestorPortfolio from "./pages/investor/InvestorPortfolio";
import NdaSign from "./pages/NdaSign";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiesPolicy from "./pages/CookiesPolicy";
import LegalNotice from "./pages/LegalNotice";
import DataRoom from "./pages/DataRoom";
import AccessLog from "./pages/seller/AccessLog";
import { CookieBanner } from "./components/CookieBanner";
import { LanguageProvider } from "./lib/i18n";

function AppRouter() {
  const location = useLocation();

  // Detect session_id in URL fragment OR query synchronously during render
  // (Emergent Auth uses '#session_id=' but some intermediaries flip '#' to '?')
  if (location.hash?.includes("session_id=") || location.search?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
      <Route path="/aviso-legal" element={<LegalNotice />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/role-select"
        element={
          <RoleSelectWrapper />
        }
      />

      {/* Seller */}
      <Route path="/seller/confidentiality" element={<ProtectedRoute role="seller" allowWithoutNDA><ConfidentialityNDA /></ProtectedRoute>} />
      <Route path="/seller/dashboard" element={<ProtectedRoute role="seller"><SellerDashboard /></ProtectedRoute>} />
      <Route path="/seller/companies" element={<ProtectedRoute role="seller"><SellerCompanies /></ProtectedRoute>} />
      <Route path="/seller/companies/new" element={<ProtectedRoute role="seller"><NewCompany /></ProtectedRoute>} />
      <Route path="/seller/company/:id" element={<ProtectedRoute role="seller"><CompanyWizard /></ProtectedRoute>} />
      <Route path="/seller/company/:id/questionnaire" element={<ProtectedRoute role="seller"><Questionnaire /></ProtectedRoute>} />
      <Route path="/seller/company/:id/teaser" element={<ProtectedRoute role="seller"><TeaserReview /></ProtectedRoute>} />
      <Route path="/seller/company/:id/im" element={<ProtectedRoute role="seller"><ImReview /></ProtectedRoute>} />
      <Route path="/seller/company/:id/accesos" element={<ProtectedRoute role="seller"><AccessLog /></ProtectedRoute>} />

      {/* Searcher */}
      <Route path="/searcher/onboarding" element={<ProtectedRoute role="searcher" allowWithoutSearcherProfile><SearcherOnboarding /></ProtectedRoute>} />
      <Route path="/searcher/marketplace" element={<ProtectedRoute role="searcher"><SearcherDashboard /></ProtectedRoute>} />
      <Route path="/searcher/company/:id" element={<ProtectedRoute role="searcher"><CompanyDetail /></ProtectedRoute>} />
      <Route path="/searcher/pipeline" element={<ProtectedRoute role="searcher"><SearcherPipeline /></ProtectedRoute>} />

      {/* Investor */}
      <Route path="/investor/onboarding" element={<ProtectedRoute role="investor" allowWithoutInvestorTerms><InvestorOnboarding /></ProtectedRoute>} />
      <Route path="/investor/marketplace" element={<ProtectedRoute role="investor"><InvestorMarketplace /></ProtectedRoute>} />
      <Route path="/investor/opportunity/:id" element={<ProtectedRoute role="investor"><InvestorOpportunityDetail /></ProtectedRoute>} />
      <Route path="/investor/portfolio" element={<ProtectedRoute role="investor"><InvestorPortfolio /></ProtectedRoute>} />

      {/* NDA (searcher + investor) */}
      <Route path="/nda/:companyId" element={<ProtectedRoute><NdaSign /></ProtectedRoute>} />
      <Route path="/im/:companyId" element={<ProtectedRoute><ImView /></ProtectedRoute>} />
      <Route path="/dataroom/:companyId" element={<ProtectedRoute><DataRoom /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// RoleSelect must be authenticated but doesn't require role
function RoleSelectWrapper() {
  return <RoleSelect />;
}

function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
